import '.h{display:grid;}.ssr-plugin'
import '.j{gap:1.5rem;}.ssr-plugin'
import '.i6{grid-template-columns:repeat(4, minmax(250px, 1fr));}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { SectionHeader } from '@app/components/blocks/sectionHeader/SectionHeader'
import { pages } from '@app/util/links'
import { HousingFormCard } from '@app/components/blocks/card/HousingFormCard'
import { Style, styled } from 'inlines'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import type { HousingFormSpotlightModel } from 'functions/app/spotlight/list/listTypes'
import { HousingFormListModel } from 'functions/app/housing-form/list'
import defaultHousingFormPicture from '@app/assets/defaultHousingFormPicture.png'
import useMediaQuery from '@app/util/useMediaQuery'
import Column from '@app/components/layout/Column'
import { Empty } from '@app/components/blocks/empty/Empty'
import PeopleLookingForAHouseImage from '@app/assets/images/people_looking_for_house.png'

const COUNT = 3

const HousingFormCardItem = ({
  housingForm,
  to,
  style,
}: {
  housingForm: Omit<HousingFormListModel, 'initiatives'>
  to: string
  style?: Style
}) => {
  const followerCountQuery = useQuery('count-followers', {
    entityType: 'housingForm',
    entityIds: [housingForm.id],
  })

  return (
    <HousingFormCard
      image={housingForm.images?.[0] ?? defaultHousingFormPicture}
      title={housingForm.title}
      followers={housingForm.followers ?? []}
      followerCount={followerCountQuery.data?.[0] ?? 0}
      to={to}
      style={style}
    />
  )
}

export const HousingFormsSection = () => {
  const isMobile = useMediaQuery()
  const t = useTranslations().app
  const spotlightQuery = useQuery('list-spotlight', {
    entityType: 'housingForm',
    limit: COUNT,
  })
  const fallbackQuery = useQuery('list-housing-forms', {
    top: COUNT,
  })

  const housingForms: Omit<HousingFormListModel, 'initiatives'>[] =
    useMemo(() => {
      const spotlights: HousingFormSpotlightModel[] = spotlightQuery.data ?? []
      const fallback: HousingFormListModel[] = fallbackQuery.data ?? []
      const spotlighted = spotlights.map((s) => s.housingForm)

      if (spotlighted.length === COUNT) {
        return spotlighted
      }

      const nonDuplicated = fallback.filter(
        (fi) => spotlighted.findIndex((i) => i.id === fi.id) === -1,
      )

      return [...spotlighted, ...nonDuplicated].slice(0, COUNT)
    }, [spotlightQuery.data, fallbackQuery.data])

  return (
    <Column alignItems="stretch">
      <SectionHeader
        title={t.landingPage.housingFormsSection.title}
        subtitle={t.landingPage.housingFormsSection.subTitle}
        seeMore={t.landingPage.housingFormsSection.seeMore}
        toRoute={pages.housingForms}
      />
      {housingForms.length === 0 ? (
        <Empty
          title={t.landingPage.housingFormsSection.emptyTitle}
          imageSrc={PeopleLookingForAHouseImage}
        />
      ) : (
        <styled.div className="h j i6"
          style={{
            '@media (max-width: 1200px)': {
              gridTemplateColumns: 'repeat(2, minmax(250px, 1fr))',
            },
            '@media (max-width: 800px)': {
              gridTemplateColumns: 'repeat(1, minmax(250px, 1fr))',
            },
          }}
        >
          {housingForms.map((housingForm, index) => (
            <HousingFormCardItem
              key={housingForm.id}
              housingForm={housingForm}
              to={pages.housingForm(housingForm.slug)}
              style={{
                gridColumn: index === 0 && !isMobile ? '1 / span 2' : null,
              }}
            />
          ))}
        </styled.div>
      )}
    </Column>
  )
}
